import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Container,
  CssBaseline,
  useTheme,
  useMediaQuery,
  Divider
} from '@mui/material';
import {
  School as SchoolIcon,
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  Login as LoginIcon,
  Microsoft as MicrosoftIcon
} from '@mui/icons-material';
import { useAuth } from './AuthContext';
import authService from './services/authService';
import office365AuthService from './services/office365AuthService';

const TeacherLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleSubmit = async () => {
    if (!email || !password) {
      setError('Please enter both email and password.');
      return;
    }

    setError('');
    setLoading(true);
    
    try {
      // Use the security groups authentication system
      const result = await authService.login(email, password);
      
      if (result.success) {
        // Store teacher email for other parts of the app
        localStorage.setItem("teacherEmail", email);
        localStorage.setItem("teacherName", result.user.name);
        
        // Login with auth context
        login("teacher", "authenticated", email);
      } else {
        setError(result.message || 'Invalid credentials');
      }
    } catch (err) {
      setError('Login failed. Please try again.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleOffice365Login = async () => {
    setError('');
    setLoading(true);

    try {
      // Store flag to indicate teacher login was initiated
      localStorage.setItem("office365LoginType", "teacher");
      // Use redirect instead of popup to avoid popup blockers
      await office365AuthService.loginWithRedirect();
      // After redirect, the user will be redirected back and index.js will handle the response
    } catch (err) {
      setError('Office 365 login failed. Please try again.');
      console.error('Office 365 login error:', err);
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.1) 0%, rgba(0,0,0,0) 50%)',
          pointerEvents: 'none',
        }
      }}
    >
      <CssBaseline />
      <Container maxWidth="xs">
        <Paper
          elevation={24}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            borderRadius: 4,
            transition: 'transform 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-5px)'
            }
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: '#1a237e',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
            }}
          >
            <SchoolIcon sx={{ fontSize: 40, color: '#fff' }} />
          </Box>

          <Typography component="h1" variant="h4" sx={{ mb: 1, fontWeight: 700, color: '#1a237e' }}>
            Teacher Portal
          </Typography>
          
          <Typography variant="body2" color="textSecondary" sx={{ mb: 4 }}>
            Sign in to access your dashboard
          </Typography>

          <Box component="form" noValidate sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#fff',
                  '&:hover fieldset': {
                    borderColor: '#1a237e',
                  },
                }
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#fff',
                  '&:hover fieldset': {
                    borderColor: '#1a237e',
                  },
                }
              }}
            />

            {error && (
              <Typography 
                color="error" 
                variant="body2" 
                sx={{ 
                  mt: 2, 
                  textAlign: 'center',
                  backgroundColor: 'rgba(211, 47, 47, 0.1)',
                  padding: 1,
                  borderRadius: 1
                }}
              >
                {error}
              </Typography>
            )}

            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              startIcon={!loading && <LoginIcon />}
              sx={{
                mt: 4,
                mb: 2,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 2,
                backgroundColor: '#1a237e',
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(26, 35, 126, 0.3)',
                '&:hover': {
                  backgroundColor: '#0d47a1',
                  boxShadow: '0 6px 16px rgba(26, 35, 126, 0.4)',
                },
              }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>

            <Divider sx={{ my: 2 }}>
              <Typography variant="body2" color="textSecondary">
                OR
              </Typography>
            </Divider>

            <Button
              fullWidth
              variant="outlined"
              onClick={handleOffice365Login}
              disabled={loading}
              startIcon={<MicrosoftIcon />}
              sx={{
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: 2,
                borderColor: '#0078d4',
                color: '#0078d4',
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#005a9e',
                  backgroundColor: 'rgba(0, 120, 212, 0.04)',
                },
              }}
            >
              Sign in with Office 365
            </Button>
          </Box>
        </Paper>
        <Typography variant="body2" color="white" align="center" sx={{ mt: 4, opacity: 0.8 }}>
          © {new Date().getFullYear()} School Management System
        </Typography>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Link to="/privacy-policy" style={{ color: 'white', opacity: 0.8, textDecoration: 'none', fontSize: '0.875rem' }}>
            Privacy Policy
          </Link>
        </Box>
      </Container>
    </Box>
  );
};

export default TeacherLogin;
